// pages/DeviceManagerPage.js
import React, { useState, useEffect } from 'react'
import {
  getFirestore,
  collection,
  query,
  orderBy,
  onSnapshot
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import toast from 'react-hot-toast'
import { app } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import UserService from '../services/UserService'
import './DeviceManagerPage.css'

const hasUmlauts = str => {
  const umlautRegex = /[äöüÄÖÜß]/g
  return umlautRegex.test(str)
}

function DeviceManagerPage () {
  const { user } = useAuth()
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingDevice, setEditingDevice] = useState(null)
  const [formErrors, setFormErrors] = useState({})
  const [formData, setFormData] = useState({
    displayName: '',
    customDisplayName: '',
    manufacturer: '',
    category: '',
    description: '',
    tags: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState('all')

  const db = getFirestore(app)
  const functions = getFunctions(app)

  // Real-time Devices laden (company-scoped)
  useEffect(() => {
    if (!user) {
      setLoading(false)
      return
    }

    let unsubscribe = () => {}

    const setupListener = async () => {
      try {
        const companyId = await UserService.getUserCompanyId(user.uid)
        const devicesRef = collection(db, 'companies', companyId, 'devices')
        const q = query(devicesRef, orderBy('displayName', 'asc'))

        unsubscribe = onSnapshot(
          q,
          snapshot => {
            const devicesData = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            setDevices(devicesData)
            setLoading(false)
          },
          error => {
            console.error('Error loading devices:', error)
            setLoading(false)
          }
        )
      } catch (error) {
        console.error('Error setting up devices listener:', error)
        setLoading(false)
      }
    }

    setupListener()

    return () => unsubscribe()
  }, [db, user])

  useEffect(() => {
    if (!editingDevice && showAddModal) {
      const errors = {}
      if (formData.displayName && hasUmlauts(formData.displayName)) {
        errors.displayName = 'Umlaute (ä, ö, ü, ß) sind nicht erlaubt.'
      }
      if (formData.manufacturer && hasUmlauts(formData.manufacturer)) {
        errors.manufacturer = 'Umlaute (ä, ö, ü, ß) sind nicht erlaubt.'
      }
      setFormErrors(errors)
    } else {
      setFormErrors({})
    }
  }, [formData.displayName, formData.manufacturer, editingDevice, showAddModal])

  const handleSubmit = async e => {
    e.preventDefault()
    setSubmitting(true)

    try {
      // --- Validierung nur beim HINZUFÜGEN ---
      if (!editingDevice && Object.keys(formErrors).length > 0) {
        setSubmitting(false)
        return
      }
      if (editingDevice) {
        // Update existing device
        const updateDeviceFn = httpsCallable(functions, 'updateDevice')
        await updateDeviceFn({
          deviceId: editingDevice.id,
          updates: {
            ...formData,
            tags: formData.tags
              .split(',')
              .map(t => t.trim())
              .filter(Boolean)
          }
        })
        toast.success('Gerät wurde aktualisiert!')
      } else {
        // Add new device
        const addDeviceFn = httpsCallable(functions, 'addDevice')
        const result = await addDeviceFn({
          ...formData,
          tags: formData.tags
            .split(',')
            .map(t => t.trim())
            .filter(Boolean)
        })
        toast.success(result.data.message)
      }

      // Reset form
      setFormData({
        displayName: '',
        customDisplayName: '',
        manufacturer: '',
        category: '',
        description: '',
        tags: ''
      })
      setShowAddModal(false)
      setEditingDevice(null)
    } catch (error) {
      console.error('Error:', error)
      toast.error('Fehler: ' + error.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleEdit = device => {
    setEditingDevice(device)
    setFormData({
      displayName: device.displayName,
      customDisplayName: device.customDisplayName || '',
      manufacturer: device.manufacturer,
      category: device.category || '',
      description: device.description || '',
      tags: device.tags?.join(', ') || ''
    })
    setShowAddModal(true)
  }

  const handleDelete = async device => {
    if (!window.confirm(`Gerät "${device.displayName}" wirklich löschen?`)) {
      return
    }

    try {
      const deleteDeviceFn = httpsCallable(functions, 'deleteDevice')
      await deleteDeviceFn({ deviceId: device.id })
      toast.success('Gerät wurde gelöscht!')
    } catch (error) {
      console.error('Error:', error)
      toast.error('Fehler: ' + error.message)
    }
  }

  const handleToggleStatus = async device => {
    try {
      const updateDeviceFn = httpsCallable(functions, 'updateDevice')
      await updateDeviceFn({
        deviceId: device.id,
        updates: {
          status: device.status === 'active' ? 'inactive' : 'active'
        }
      })
    } catch (error) {
      console.error('Error:', error)
      toast.error('Fehler: ' + error.message)
    }
  }

  // Filtering
  const filteredDevices = devices.filter(device => {
    const displayNameToSearch = device.customDisplayName || device.displayName
    const matchesSearch =
      displayNameToSearch.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.manufacturer.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesCategory =
      filterCategory === 'all' || device.category === filterCategory

    return matchesSearch && matchesCategory
  })

  const categories = [...new Set(devices.map(d => d.category))].filter(Boolean)

  if (loading) {
    return <div className='loading'>Lade Geräte...</div>
  }

  return (
    <div className='device-manager-page'>
      <div className='page-header'>
        <h1>📱 Geräteverwaltung</h1>
        <button
          className='add-device-btn'
          onClick={() => {
            setEditingDevice(null)
            setFormData({
              displayName: '',
              customDisplayName: '',
              manufacturer: '',
              category: '',
              description: '',
              tags: ''
            })
            setShowAddModal(true)
          }}
        >
          + Neues Gerät hinzufügen
        </button>
      </div>

      {/* Filters */}
      <div className='filters'>
        <input
          type='text'
          placeholder='🔍 Suche nach Gerät oder Hersteller...'
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className='search-input'
        />

        <select
          value={filterCategory}
          onChange={e => setFilterCategory(e.target.value)}
          className='category-filter'
        >
          <option value='all'>Alle Kategorien</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div className='stats-cards'>
        <div className='stat-card'>
          <div className='stat-value'>{devices.length}</div>
          <div className='stat-label'>Gesamt</div>
        </div>
        <div className='stat-card'>
          <div className='stat-value'>
            {devices.filter(d => d.status === 'active').length}
          </div>
          <div className='stat-label'>Aktiv</div>
        </div>
        <div className='stat-card'>
          <div className='stat-value'>
            {devices.reduce(
              (sum, d) => sum + (d.stats?.conversationCount || 0),
              0
            )}
          </div>
          <div className='stat-label'>Conversations</div>
        </div>
      </div>

      {/* Devices Grid */}
      <div className='devices-grid'>
        {filteredDevices.map(device => (
          <div key={device.id} className={`device-card ${device.status}`}>
            <div className='device-header'>
              <h3>{device.customDisplayName || device.displayName}</h3>
              <span className={`status-badge ${device.status}`}>
                {device.status === 'active' ? '✓ Aktiv' : '○ Inaktiv'}
              </span>
            </div>

            <div className='device-info'>
              <p>
                <strong>Hersteller:</strong> {device.manufacturer}
              </p>
              <p>
                <strong>Kategorie:</strong> {device.category || 'N/A'}
              </p>
              {device.description && (
                <p className='device-description'>{device.description}</p>
              )}
            </div>

            <div className='device-stats'>
              <div className='stat-item'>
                <span className='stat-icon'>💬</span>
                <span>
                  {device.stats?.conversationCount || 0} Conversations
                </span>
              </div>
              <div className='stat-item'>
                <span className='stat-icon'>📚</span>
                <span>{device.stats?.knowledgeEntries || 0} Knowledge</span>
              </div>
            </div>

            {device.tags && device.tags.length > 0 && (
              <div className='device-tags'>
                {device.tags.map(tag => (
                  <span key={tag} className='tag'>
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div className='device-actions'>
              <button className='btn-edit' onClick={() => handleEdit(device)}>
                ✏️ Bearbeiten
              </button>
              <button
                className='btn-toggle'
                onClick={() => handleToggleStatus(device)}
              >
                {device.status === 'active'
                  ? '⏸️ Deaktivieren'
                  : '▶️ Aktivieren'}
              </button>
              <button
                className='btn-delete'
                onClick={() => handleDelete(device)}
              >
                🗑️ Löschen
              </button>
            </div>

            <div className='device-meta'>
              <small>
                Hinzugefügt:{' '}
                {device.addedAt?.toDate().toLocaleDateString('de-DE')} von{' '}
                {device.addedByName || device.addedBy}
              </small>
              {device.modifiedBy && device.modifiedBy !== device.addedBy && (
                <small style={{ marginLeft: '0.5rem', color: '#666' }}>
                  • Zuletzt bearbeitet von{' '}
                  {device.modifiedByName || device.modifiedBy}
                </small>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className='modal-overlay' onClick={() => setShowAddModal(false)}>
          <div className='modal-content' onClick={e => e.stopPropagation()}>
            <h2>
              {editingDevice ? 'Gerät bearbeiten' : 'Neues Gerät hinzufügen'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className='form-group'>
                <label>Gerätename *</label>
                <input
                  readOnly={editingDevice}
                  disabled={editingDevice}
                  type='text'
                  value={formData.displayName}
                  onChange={e =>
                    setFormData({ ...formData, displayName: e.target.value })
                  }
                  required
                  placeholder='z.B. XN-2000'
                  style={{ borderColor: formErrors.displayName ? 'red' : '' }}
                />
                {formErrors.displayName && (
                  <p className='error-text'>{formErrors.displayName}</p>
                )}
                {editingDevice && (
                  <p className='form-hint'>
                    Der Gerätename kann nachträglich nicht geändert werden.
                  </p>
                )}
              </div>

              <div className='form-group'>
                <label>Anzeigename (optional)</label>
                <input
                  type='text'
                  value={formData.customDisplayName}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      customDisplayName: e.target.value
                    })
                  }
                  placeholder='z.B. Sysmex XN-2000 Hematology Analyzer'
                />
                <p className='form-hint'>
                  Ein benutzerdefinierter Anzeigename für dieses Gerät. Falls
                  leer, wird der Gerätename angezeigt.
                </p>
              </div>

              <div className='form-group'>
                <label>Hersteller *</label>
                <input
                  readOnly={editingDevice}
                  disabled={editingDevice}
                  type='text'
                  value={formData.manufacturer}
                  onChange={e =>
                    setFormData({ ...formData, manufacturer: e.target.value })
                  }
                  required
                  placeholder='z.B. Sysmex'
                  style={{ borderColor: formErrors.manufacturer ? 'red' : '' }}
                />
                {formErrors.manufacturer && (
                  <p className='error-text'>{formErrors.manufacturer}</p>
                )}
              </div>

              <div className='form-group'>
                <label>Kategorie</label>
                <input
                  type='text'
                  value={formData.category}
                  onChange={e =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  placeholder='z.B. Hematology Analyzer'
                />
              </div>

              <div className='form-group'>
                <label>Beschreibung</label>
                <textarea
                  value={formData.description}
                  onChange={e =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder='Kurze Beschreibung des Geräts...'
                  rows={3}
                />
              </div>

              <div className='form-group'>
                <label>Tags (kommagetrennt)</label>
                <input
                  type='text'
                  value={formData.tags}
                  onChange={e =>
                    setFormData({ ...formData, tags: e.target.value })
                  }
                  placeholder='z.B. analyzer, hematology, automated'
                />
              </div>

              <div className='modal-actions'>
                <button
                  type='button'
                  className='btn-cancel'
                  onClick={() => setShowAddModal(false)}
                  disabled={submitting}
                >
                  Abbrechen
                </button>
                <button
                  type='submit'
                  className='btn-submit'
                  disabled={
                    submitting ||
                    (!editingDevice && Object.keys(formErrors).length > 0)
                  }
                >
                  {submitting
                    ? 'Wird gespeichert...'
                    : editingDevice
                    ? 'Aktualisieren'
                    : 'Hinzufügen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default DeviceManagerPage
